import JsonResponse from "csi/comm/JsonResponse";
import { ControllerSaveFunction } from "csi/middleware/common";
import { ATTR_PARENT, C8Node, cnl, ICache } from "csi/typings";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable } from "mobx";
import { RestoreFromBOMResources, RestoreFromBOMStore } from "../Restore/RestoreFromBOMStore";
import FormData from "csi/action/FormData";
import reflection from "csi/reflection";
import def from "csi/def";
import { View } from "csi/def/View";
import { createContext, useContext } from "react";
import { createRestoreFromSourceBOMStore, pullFromSourceBOM } from "./PullFromBOMService";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";


export interface PullFromBOMResources extends RestoreFromBOMResources {
	tableSubTitleRes: string;
}

export class PullFromBOMStore extends WizardStore {
	private readonly cache: ICache;
	readonly formData: FormData;
	/** URL of the BOM revision to be updated */
	readonly hostBOMRevisionUrl: cnl;
	/** First step table node type */
	readonly tableNodeType: string;
	/** First step table subtitle resource */
	readonly tableSubTitleRes: string;
	/** First step table view id */
	tableViewId: string;
	/** Resources object passed from the action */
	resources: PullFromBOMResources;
	/** RestoreFromBOMStore instance  for second step */
	restoreFromBOMStore: RestoreFromBOMStore;
	/** Selected node from the first step table */
	@observable.ref fromBOM: C8Node;

	constructor(
		actionId: string,
		onClose: () => void,
		onSave: ControllerSaveFunction,
		cache: ICache,
		formData: FormData,
		locale: ILocaleProvider,
		hostBOMRevisionUrl: cnl,
		resources: PullFromBOMResources,
	) {
		super(2, actionId, onClose, onSave, locale);
		makeObservable(this);
		this.formData = formData;
		this.resources = resources;
		this.tableNodeType = "ApparelBOM";
		this.tableViewId = "LinkedSourceDataSheets";
		this.hostBOMRevisionUrl = hostBOMRevisionUrl;
		this.tableSubTitleRes = resources.tableSubTitleRes;
		this.cache = cache;
	}

	/* Collect the input from the Wizard Forms and sent the request to Pull From BOM */
	onSavePromise = async (): Promise<JsonResponse> => {
		const { restoreFromBOMStore } = this;
		const itemsToUpdate = restoreFromBOMStore.filteredUpdateItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
		const itemsToAdd = restoreFromBOMStore.filteredAddItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
		const itemsToDelete = restoreFromBOMStore.filteredDeleteItems;
		const tableSelectedNode = this.tableSelectedNodes.at(0);
		try {
			return this._pullUpdatesOperation(restoreFromBOMStore.createRevision, this.hostBOMRevisionUrl, restoreFromBOMStore.restoreAllPlacements, itemsToAdd, itemsToUpdate, tableSelectedNode, itemsToDelete);
		} catch (err) {
			throw err;
		}
	};

	/** Get Table View params */
	get viewParams(): Object {
		const rc = reflection.getClass(this.tableNodeType);
		const defView = def.getClassViewDef(rc, this.tableViewId) as View;

		defView.setFormData(this.formData);
		return {
			bPopup: true,
			defView: defView,
		};
	}

	/** 
	 * @override
	 * extract information from aggregate Form and initialize restoreFromBOMStore
	 */
	protected onNextPage = async (index: number): Promise<boolean> => {
		const { formData, fromBOM, locale } = this;
		if (index === 0) {
			const selectedNode = formData.getSelectedNode();

			if (!selectedNode) {
				return false;
			}

			if (!fromBOM || selectedNode.$URL !== fromBOM.$URL) {
				this.fromBOM = selectedNode;
				this.restoreFromBOMStore = await this.initializeRestoreFromBOMStore();
			}
		}

		return true;
	};

	/** Get the node to be updated  from the cache*/
	get master(): C8Node {
		return this.cache.get(this.currentRevisonNode[ATTR_PARENT]);
	}

	/** Get the node revision to be updated  from the cache*/
	get currentRevisonNode(): C8Node {
		return this.cache.get(this.hostBOMRevisionUrl);
	}


	/** Get the current revision URL of the BOM to update from */
	@computed
	get fromBOMCurrentRevisionUrl(): cnl {
		return this.fromBOM?.CurrentRevision;
	}

	@computed
	get selectedFromBOM(): C8Node {
		return this.formData.getSelectedNode();
	};

	/** Get formData selected Nodes */
	get tableSelectedNodes(): C8Node[] {
		return this.formData.getSelectedNodes();
	}

	_pullUpdatesOperation = async (createRevision: boolean, restoreBOMRevisionURL: cnl, restoreAllPlacements: boolean, addPlacements: CheckboxTreeNode[], updatePlacements: CheckboxTreeNode[], tableSelectedNode: C8Node, deletePlacements: CheckboxTreeNode): Promise<JsonResponse> => {
		return pullFromSourceBOM(createRevision, restoreBOMRevisionURL, restoreAllPlacements, addPlacements, updatePlacements, tableSelectedNode, deletePlacements);
	};

	@action
	async initializeRestoreFromBOMStore(): Promise<RestoreFromBOMStore> {
		const { actionId, cache, fromBOMCurrentRevisionUrl, formData, locale, currentRevisonNode, resources } = this;
		return await createRestoreFromSourceBOMStore(actionId, cache, formData, currentRevisonNode, fromBOMCurrentRevisionUrl, null, null, locale, resources);
	};

	/**
	 * @override
	 * Pass action bottom message for the second page of the wizard. 
	 */
	protected _actionMessage = (): string => {
		if (this.index === 1) {
			return this.restoreFromBOMStore?.actionMessage;
		}
		return "";
	};

	/**
	 * @override
	 * disable finish button if there is not item selected. 
	 */
	protected _disableFinishButton = (): boolean => {
		if (this.index === 1) {
			return this.restoreFromBOMStore.placementsSelected === 0;
		}
		return false;
	};
}

export const PullFromBOMStoreContext = createContext<PullFromBOMStore>(undefined);
export const usePullFromBOMStoreContext = (): PullFromBOMStore => useContext(PullFromBOMStoreContext);